import Compressor from "compressorjs";

import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import { CovidVerificationMode } from "@/data/model/Enums";
import type { ICertificateEncodingProgressEvent } from "@/data/model/Events";
import type { ICovidCertificateVerifyResult } from "@/data/model/Profile";
import { exceptionTypes } from "@/data/util/Config";

import { logError } from "@/shared/logging/errors";

import { TastierClient } from "@/app/TastierClient";
import { trans } from "@/app/localization/Trans";
import { UserCovidCertificateService } from "@/app/services/userCovidCerificateService";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";

export class CovidCertEncrypter {
    public onVerified?: { (result: ICovidCertificateVerifyResult) };
    public onReadyStateChanged?: { (isReady: boolean) };
    public onProgressChanged?: { (ev: ICertificateEncodingProgressEvent) };
    public onSizeChanged?: { () };

    public result?: ICovidCertificateVerifyResult;
    private $element: JQuery;

    private $certificatePreview?: JQuery;
    private $certificateInput?: JQuery;

    private $secondaryCertificatePreview?: JQuery;
    private $secondaryCertificateInput?: JQuery;

    private $idPreview?: JQuery;
    private $idInput?: JQuery;

    private pendingCertImage?: File;
    private pendingSecondaryCertImage?: File;
    private pendingIdImage?: File;
    userCovidCertificateService: UserCovidCertificateService;

    constructor(forMode: CovidVerificationMode) {
        if (forMode === undefined) forMode = CovidVerificationMode.ThreeG;

        this.userCovidCertificateService = UserCovidCertificateService.getInstance();

        this.$element = $('<div class="covidcertencrypter" />');

        this.init(forMode);
    }

    private async init(forMode: CovidVerificationMode) {
        const profile = TastierClient.identityContext.getProfile();
        try {
            const crt = await this.userCovidCertificateService.validateCovidCertificate(forMode);
            if (profile !== undefined && profile.identityCard !== undefined) {
                if (this.onVerified !== undefined) this.onVerified(crt);
            } else {
                this.setReadyToUpload();
            }
        } catch (ex) {
            logError(ex);
            this.setReadyToUpload();
        }
    }

    private setReadyToUpload() {
        this.pendingIdImage = undefined;
        this.pendingCertImage = undefined;
        this.pendingSecondaryCertImage = undefined;

        let certRow = $('<div class="uploadrow" />').appendTo(this.$element.empty());
        this.$certificatePreview = $('<div class="preview empty"><i class="fal fa-plus-circle"></i></div>').appendTo(
            certRow
        );
        certRow.append(
            '<div class="desc"><div class="name"><i class="fas fa-qrcode"></i>' +
                trans.covidSignupPopover.uploadCertName +
                "</div>" +
                trans.covidSignupPopover.uploadCertDesc +
                "</div>"
        );

        this.$certificateInput = $('<input type="file" />').appendTo(certRow);
        this.$certificatePreview.bind("click", () => this.$certificateInput!.trigger("click"));
        this.$certificateInput.change(async () => {
            try {
                const file = await this.compressImage(this.$certificateInput!.prop("files"), {
                    maxWidth: 1280,
                    maxHeight: 2400,
                });

                this.pendingCertImage = file;

                const reader = new FileReader();
                reader.onload = () => {
                    this.$certificatePreview!.removeClass("empty")
                        .addClass("ready")
                        .css("background-image", "url('" + reader.result + "')")
                        .empty();
                    this.$certificatePreview!.append('<div class="remover"><i class="fas fa-minus-circle"></i></div>');
                };

                reader.readAsDataURL(file);

                if (this.onReadyStateChanged !== undefined) this.onReadyStateChanged(this.isValid());
            } catch (ex) {
                logError(ex);
                this.$certificatePreview!.removeClass("ready")
                    .addClass("empty")
                    .html('<i class="fal fa-plus-circle"></i>');
                this.pendingCertImage = undefined;
            }
        });

        if (Helper.getCovidServiceConfigValue("verifyCovidCertMode") === CovidVerificationMode.TwoGPlus) {
            certRow = $('<div class="uploadrow" />').appendTo(this.$element.height(360));
            this.$secondaryCertificatePreview = $(
                '<div class="preview empty"><i class="fal fa-plus-circle"></i></div>'
            ).appendTo(certRow);
            certRow.append(
                '<div class="desc"><div class="name"><i class="fas fa-qrcode"></i>' +
                    trans.covidSignupPopover.uploadSecondaryCertName +
                    "</div>" +
                    trans.covidSignupPopover.uploadSecondaryCertDesc +
                    "</div>"
            );

            this.$secondaryCertificateInput = $('<input type="file" />').appendTo(certRow);
            this.$secondaryCertificatePreview.bind("click", () => this.$secondaryCertificateInput!.trigger("click"));
            this.$secondaryCertificateInput.change(async () => {
                try {
                    const file = await this.compressImage(this.$secondaryCertificateInput!.prop("files"), {
                        maxWidth: 1280,
                        maxHeight: 2400,
                    });

                    this.pendingSecondaryCertImage = file;

                    const reader = new FileReader();
                    reader.onload = () => {
                        this.$secondaryCertificatePreview!.removeClass("empty")
                            .addClass("ready")
                            .css("background-image", "url('" + reader.result + "')")
                            .empty();
                        this.$secondaryCertificatePreview!.append(
                            '<div class="remover"><i class="fas fa-minus-circle"></i></div>'
                        );
                    };

                    reader.readAsDataURL(file);

                    if (this.onReadyStateChanged !== undefined) this.onReadyStateChanged(this.isValid());
                } catch (ex) {
                    logError(ex);
                    this.$secondaryCertificatePreview!.removeClass("ready")
                        .addClass("empty")
                        .html('<i class="fal fa-plus-circle"></i>');
                    this.pendingSecondaryCertImage = undefined;
                }
            });
        }

        const idRow = $('<div class="uploadrow" />').appendTo(this.$element);
        this.$idPreview = $('<div class="preview empty"><i class="fal fa-plus-circle"></i></div>').appendTo(idRow);
        idRow.append(
            '<div class="desc"><div class="name"><i class="fal fa-id-card"></i>' +
                trans.covidSignupPopover.uploadIdName +
                "</div>" +
                trans.covidSignupPopover.uploadIdDesc +
                "</div>"
        );

        this.$idInput = $('<input type="file" />').appendTo(idRow);
        this.$idPreview.bind("click", () => this.$idInput!.trigger("click"));
        this.$idInput.change(async () => {
            try {
                const file = await this.compressImage(this.$idInput!.prop("files"), {
                    maxWidth: 1600,
                    maxHeight: 1600,
                    quality: 1,
                });

                this.pendingIdImage = file;

                const reader = new FileReader();
                reader.onload = () => {
                    this.$idPreview!.removeClass("empty")
                        .addClass("ready")
                        .css("background-image", "url('" + reader.result + "')")
                        .empty();
                    this.$idPreview!.append('<div class="remover"><i class="fas fa-minus-circle"></i></div>');
                };

                reader.readAsDataURL(file);

                if (this.onReadyStateChanged !== undefined) this.onReadyStateChanged(this.isValid());
            } catch (ex) {
                logError(ex);
                this.$idPreview!.removeClass("ready").addClass("empty").html('<i class="fal fa-plus-circle"></i>');
                this.pendingIdImage = undefined;
            }
        });

        if (this.onReadyStateChanged !== undefined) this.onReadyStateChanged(this.isValid());

        if (this.onSizeChanged !== undefined) this.onSizeChanged();
    }

    private onProgressChange(ev: ICertificateEncodingProgressEvent) {
        if (this.onProgressChanged !== undefined) this.onProgressChanged(ev);
    }

    public setError(): CovidCertEncrypter {
        return this;
    }

    public isValid(): boolean {
        if (this.result === undefined) {
            if (this.pendingCertImage === undefined) return false;

            if (this.pendingIdImage === undefined) return false;
        } else return this.result.isValid;

        return true;
    }

    public encrypt(): Promise<ICovidCertificateVerifyResult> {
        return new Promise((resolve, reject) => {
            //bind progress update handler
            TastierClient.context.socket.onCertificateEncodingProgress = (ev) => {
                this.onProgressChange(ev);
            };

            if (this.pendingIdImage === undefined || this.pendingCertImage === undefined) {
                TastierClient.context.socket.onCertificateEncodingProgress = undefined;
                reject(new Error());
                return;
            }

            this.onProgressChange({ progress: 0, state: "uploading_files" });

            return Factory.getInstance<ProfileDao>(ProfileDao)
                .encryptCovidCert({
                    certificate: this.pendingCertImage,
                    secondaryCertificate: this.pendingSecondaryCertImage,
                    identityCard: this.pendingIdImage,
                })
                .then((r) => {
                    TastierClient.context.socket.onCertificateEncodingProgress = undefined;

                    const profile = TastierClient.identityContext.getProfile();
                    if (r.isValid) {
                        const newProfile = profile!;
                        newProfile.firstName = r.firstName;
                        newProfile.lastName = r.lastName;
                        newProfile.birthday = r.birthday;
                        newProfile.identityCard = r.identityCard;
                        newProfile.lastCovidCertValidation = new Date();

                        this.userCovidCertificateService.storeCovidCertificate(r);
                        TastierClient.identityContext.aggregateIdentity(newProfile, true);

                        resolve(r);
                    } else {
                        this.userCovidCertificateService.storeCovidCertificate();
                        Popup.show(trans.covidSignupPopover.certificateExpired);
                        this.setReadyToUpload();
                        reject(new Error());
                    }
                })
                .catch((ex) => {
                    logError(ex);
                    const e = ex as DaoError;
                    let exceptionMessage = trans.covidSignupPopover.failedUpload;

                    let clearIdDoc = false;
                    let clearCertificateDoc = false;
                    TastierClient.context.socket.onCertificateEncodingProgress = undefined;

                    if (e.type === exceptionTypes.covid_encryption_exception && e.additionalData.reason !== undefined) {
                        switch (e.additionalData.reason) {
                            case "covid_encryption_exception":
                                clearIdDoc = true;
                                clearCertificateDoc = true;
                                exceptionMessage = trans.covidSignupPopover.failedUpload;
                                break;
                            case "certificate_duplicate":
                                clearIdDoc = true;
                                clearCertificateDoc = true;
                                exceptionMessage = trans.covidSignupPopover.failedDuplicate;
                                break;
                            case "certificate_missmatch":
                                clearIdDoc = true;
                                clearCertificateDoc = true;
                                exceptionMessage = trans.covidSignupPopover.failedMissmatch;
                                break;
                            case "covid_mode_not_valid":
                                clearIdDoc = true;
                                clearCertificateDoc = true;
                                exceptionMessage = trans.covidSignupPopover.failedInvalidMode;
                                break;
                            case "cert_decode_failed":
                                exceptionMessage = trans.covidSignupPopover.failedToDecode;
                                clearCertificateDoc = true;
                                break;
                            case "cert_invalid":
                                exceptionMessage = trans.covidSignupPopover.failedBecauseInvalid;
                                clearCertificateDoc = true;
                                break;
                            case "qr_decode_failed":
                                exceptionMessage = trans.covidSignupPopover.failedToReadQr;
                                clearCertificateDoc = true;
                                break;
                            case "identification_document_unkown":
                                exceptionMessage = trans.covidSignupPopover.failedIdDocUnkown;
                                clearIdDoc = true;
                                break;
                            case "identification_document_invalid":
                                exceptionMessage = trans.covidSignupPopover.failedIdDocInvalid;
                                clearIdDoc = true;
                                break;

                            default:
                                clearIdDoc = true;
                                clearCertificateDoc = true;
                        }
                    }

                    this.userCovidCertificateService.storeCovidCertificate();
                    Popup.show(exceptionMessage);

                    if (clearIdDoc) {
                        this.$idPreview!.removeClass("ready")
                            .addClass("empty")
                            .removeAttr("style")
                            .html('<i class="fal fa-plus-circle"></i>');
                        this.pendingIdImage = undefined;
                    }

                    if (clearCertificateDoc) {
                        this.$certificatePreview!.removeClass("ready")
                            .addClass("empty")
                            .removeAttr("style")
                            .html('<i class="fal fa-plus-circle"></i>');
                        if (this.$secondaryCertificatePreview !== undefined)
                            this.$secondaryCertificatePreview
                                .removeClass("ready")
                                .addClass("empty")
                                .removeAttr("style")
                                .html('<i class="fal fa-plus-circle"></i>');

                        this.pendingCertImage = undefined;
                        this.pendingSecondaryCertImage = undefined;
                    }

                    reject(new Error());
                });
        });
    }

    public appendTo($element: JQuery): CovidCertEncrypter {
        $element.append(this.$element);
        return this;
    }

    private compressImage(fileList: FileList, settings: object): Promise<File> {
        return new Promise((resolve, reject) => {
            const file = fileList[0];
            if (file === undefined) {
                reject(new Error());
                return;
            }

            if (file.type.toLowerCase().indexOf("image") === -1 && file.type.toLowerCase().indexOf("pdf") === -1) {
                Popup.show(trans.profile.onlyImageFiles);

                reject(new Error());
                return;
            }

            new Compressor(file, {
                quality: 1,
                checkOrientation: true,
                success: (result) => {
                    resolve(result as File);
                },
                error: () => {
                    resolve(file);
                },
                ...settings,
            });
        });
    }
}
